export interface TabGroupOptions {
  tabWrapperIdentifier: string;
  hiddenClass: string;
}

export function initializeTabs(TabGroupOptions: TabGroupOptions) {
  const tabWrapper = document.querySelector(TabGroupOptions.tabWrapperIdentifier);
  const tabList = tabWrapper?.querySelector('[role="tablist"]');

  if (!tabWrapper || !tabList) {
    return;
  }

  // Constants
  const tabs = tabWrapper.querySelectorAll('[role="tab"]');
  const panels = tabWrapper.querySelectorAll('[role="tabpanel"]');

  // Set initial active tab and panel
  let currentActiveTab: HTMLElement | null = findInitialActiveTab();
  let currentActivePanel: HTMLElement | null = findInitialActivePanel();

  if (tabs.length === 0 || panels.length === 0) {
    return;
  }

  // ####### Event Listeners #######

  // Click event for each tab
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      setActiveTabAndPanelByIndex(index);
    });
  });

  // Add listeners for focusin and focusout on tabList
  // to be able to handle keyboard navigation only when tabs are focused
  let isTabListFocused = false;
  tabList.addEventListener('focusin', handleFocusIn);
  tabList.addEventListener('focusout', handleFocusOut);

  function handleFocusIn(event: Event) {
    if (!isTabListFocused) {
      isTabListFocused = true;
      document.addEventListener('keydown', handleKeyDown);
    }
  }

  function handleFocusOut(event: Event) {
    const relatedTarget = (event as FocusEvent).relatedTarget;
    if (tabList && !tabList.contains(relatedTarget as Node)) {
      isTabListFocused = false;
      document.removeEventListener('keydown', handleKeyDown);
    }
  }

  // ####### Functions #######

  /**
   * Set the active tab and panel by index.
   * @param index The index of the tab and panel to activate.
   * @returns void
   */
  function setActiveTabAndPanelByIndex(index: number) {
    if (index < 0 || index >= tabs.length) return;

    currentActiveTab?.setAttribute('aria-selected', 'false');
    currentActiveTab?.setAttribute('tabindex', '-1');
    currentActivePanel?.classList.add(TabGroupOptions.hiddenClass);

    tabs[index].setAttribute('aria-selected', 'true');
    tabs[index].removeAttribute('tabindex');
    panels[index].classList.remove(TabGroupOptions.hiddenClass);

    currentActiveTab = tabs[index] as HTMLElement;
    currentActivePanel = panels[index] as HTMLElement;

    currentActiveTab?.focus();
  }

  /**
   * Find the initially active tab.
   * @returns HTMLElement | null
   */
  function findInitialActiveTab(): HTMLElement | null {
    if (!tabWrapper) return null;
    const activeTab = tabWrapper.querySelector('[aria-selected="true"]');
    return activeTab ? (activeTab as HTMLElement) : null;
  }

  /**
   * Find the initially active panel associated with the active tab.
   * @returns HTMLElement | null
   */
  function findInitialActivePanel(): HTMLElement | null {
    if (!tabWrapper) return null;
    const activePanel = tabWrapper.querySelector(`[aria-labelledby="${currentActiveTab?.id}"]`);
    
    if (activePanel && activePanel.classList.contains(TabGroupOptions.hiddenClass)) {
      activePanel.classList.remove(TabGroupOptions.hiddenClass);
    }

    return activePanel ? (activePanel as HTMLElement) : null;
  }

  /**
   * Handle keyboard navigation for tabs,
   * ArrowRight - Move to next tab
   * ArrowLeft - Move to previous tab
   * Home - Move to first tab
   * End - Move to last tab
   * @param event KeyboardEvent
   * @returns void
   */
  function handleKeyDown(event: KeyboardEvent) {
    if (!currentActiveTab) return;
    if (!isTabListFocused) return;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft' && event.key !== 'Home' && event.key !== 'End') return;
    
    event.preventDefault();

    const tabsLength = tabs.length - 1;
    const currentIndex = Array.from(tabs).indexOf(currentActiveTab);
    let newIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1);
        if (newIndex > tabsLength) newIndex = 0;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1);
        if (newIndex < 0) newIndex = tabsLength;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = tabsLength;
        break;
      }
      
    setActiveTabAndPanelByIndex(newIndex);
  }
}